import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getApiBaseUrl } from './api';

// ---------------------------------------------------------------------------
// Types mirroring marketplace/models.py (amounts always in paise)
// ---------------------------------------------------------------------------

export interface Plan {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  is_public: boolean;
  currency: string;
  price: number;
  mrp: number;
  validity: number | null;
  ads_free: boolean;
  allow_team_join: boolean;
  order: number;
}

export interface Order {
  id: string;
  plan: string;            // plan id
  plan_name?: string;      // annotated by serializer
  user: number;
  coupon_code: string | null;
  billing_state: string | null;
  currency: string;
  plan_price: number;
  quantity: number;
  total_price: number;
  discount: number;
  amount: number;
  tax: number;
  gross_amount: number;
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'REFUNDED' | 'PARTIAL_REFUND';
  is_test: boolean | null;
  payment_id: string | null;
  paid_at: string | null;
  refund_id: string | null;
  refunded_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserSubscription {
  id: number;
  user: number;
  plan: string;
  plan_name?: string;
  order: string | null;
  is_active: boolean;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
}

export interface CreditNote {
  id: string;
  order: string;
  invoice: string;
  user: number;
  credit_note_no: number;
  financial_year: string;
  refund_amount: number;
  tax_reversed: number;
  gross_refund: number;
  date_issued: string;
  razorpay_refund_id: string | null;
  billing_state: string | null;
  // join fields from serializer
  plan_name?: string;
  order_display?: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ---------------------------------------------------------------------------
// Query param shapes
// ---------------------------------------------------------------------------

export interface OrderListParams {
  status?: string;
  created_at_after?: string;
  created_at_before?: string;
  is_test?: boolean;
  page?: number;
  page_size?: number;
  plan?: string;
}

export interface SubscriptionListParams {
  is_active?: boolean;
  plan?: string;
  page?: number;
  page_size?: number;
}

export interface CreditNoteListParams {
  date_issued_after?: string;
  date_issued_before?: string;
  page?: number;
  page_size?: number;
}

export interface MarketplaceStatsParams {
  status?: string;
  created_at_after?: string;
  created_at_before?: string;
  is_test?: boolean;
  date_issued_after?: string;
  date_issued_before?: string;
}

export interface MarketplaceKpi {
  total: number;
  gross: number;
  tax: number;
  net: number;
  disc: number;
}

export interface PlanBreakdownRow {
  plan_id: string;
  name: string;
  order_count: number;
  gross: number;
  active_subs: number;
  inactive_subs: number;
}

export interface RefundKpi {
  count: number;
  gross_refund: number;
  tax_reversed: number;
  net_refund: number;
}

export interface StatusDistributionRow {
  status: string;
  count: number;
}

export interface RevenueByStateRow {
  state: string;
  gross: number;
}

export interface DailyTrendRow {
  date: string;
  count: number;
  gross: number;
  new_count: number;
  new_gross: number;
  old_count: number;
  old_gross: number;
}

export interface BuyerSplit {
  new_count: number;
  new_gross: number;
  old_count: number;
  old_gross: number;
}

export interface CouponUsageRow {
  code: string;
  count: number;
  total_discount: number;
}

export interface SubscriptionHealth {
  total: number;
  active: number;
  inactive: number;
}

export interface MarketplaceStats {
  kpi: MarketplaceKpi;
  plan_breakdown: PlanBreakdownRow[];
  refund_kpi: RefundKpi;
  status_distribution: StatusDistributionRow[];
  revenue_by_state: RevenueByStateRow[];
  buyer_split: BuyerSplit;
  daily_trend: DailyTrendRow[];
  coupon_usage: CouponUsageRow[];
  subscription_health: SubscriptionHealth;
  credit_notes: CreditNote[];
}

export interface ChurnedUser {
  user: string;
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  reason: 'expired' | 'refunded';
  churned_at: string | null;
  plan: string;
  plan_name: string;
  plan_price: number;
  purchase_count: number;
  lifetime_gross: number;
  lifetime_refunded: number;
  plans_bought: string[];
  last_active: string | null;
  app_status: 'recent' | 'abandoned';
}

export interface ChurnSummary {
  churned_users: number;
  expired: number;
  refunded: number;
  still_in_app: number;
  abandoned: number;
  lifetime_gross: number;
  lifetime_refunded: number;
}

export interface ChurnResponse {
  summary: ChurnSummary;
  count: number;
  results: ChurnedUser[];
}

export interface ChurnParams {
  churned_after?: string;
  churned_before?: string;
  reason?: 'expired' | 'refunded';
  app_activity?: 'recent' | 'abandoned';
  plan?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

// ---------------------------------------------------------------------------
// RTK Query API slice (read-only, staff endpoints)
// ---------------------------------------------------------------------------

export const marketplaceApi = createApi({
  reducerPath: 'marketplaceApi',
  baseQuery: (args, api, extraOptions) =>
    fetchBaseQuery({
      baseUrl: `${getApiBaseUrl()}/marketplace/`,
      prepareHeaders: (headers) => {
        const token = localStorage.getItem('token');
        if (token) headers.set('Authorization', `Bearer ${token}`);
        return headers;
      },
    })(args, api, extraOptions),
  tagTypes: ['Order', 'Plan', 'Subscription', 'CreditNote', 'Stats', 'Churn'],
  endpoints: (builder) => ({
    // Plans
    getPlans: builder.query<Plan[], void>({
      query: () => 'plans/?page_size=100',
      transformResponse: (res: PaginatedResponse<Plan> | Plan[]) =>
        Array.isArray(res) ? res : res.results,
      providesTags: ['Plan'],
    }),

    // Orders — fetches ALL matching pages (up to 2000/month so we can safely
    // retrieve them all by requesting a large page_size)
    getOrders: builder.query<Order[], OrderListParams>({
      query: (params) => {
        const p = new URLSearchParams();
        if (params.status && params.status !== 'ALL') p.set('status', params.status);
        if (params.created_at_after)  p.set('created_at_after',  params.created_at_after);
        if (params.created_at_before) p.set('created_at_before', params.created_at_before);
        if (params.is_test !== undefined) p.set('is_test', String(params.is_test));
        p.set('page_size', '2000');
        return `orders/?${p.toString()}`;
      },
      transformResponse: (res: PaginatedResponse<Order> | Order[]) =>
        Array.isArray(res) ? res : res.results,
      providesTags: ['Order'],
    }),

    // Subscriptions
    getSubscriptions: builder.query<UserSubscription[], SubscriptionListParams>({
      query: (params) => {
        const p = new URLSearchParams();
        if (params.is_active !== undefined) p.set('is_active', String(params.is_active));
        if (params.plan) p.set('plan', params.plan);
        p.set('page_size', '5000');
        return `subscriptions/?${p.toString()}`;
      },
      transformResponse: (res: PaginatedResponse<UserSubscription> | UserSubscription[]) =>
        Array.isArray(res) ? res : res.results,
      providesTags: ['Subscription'],
    }),

    // Credit Notes (refunds)
    getCreditNotes: builder.query<CreditNote[], CreditNoteListParams>({
      query: (params) => {
        const p = new URLSearchParams();
        if (params.date_issued_after)  p.set('date_issued_after',  params.date_issued_after);
        if (params.date_issued_before) p.set('date_issued_before', params.date_issued_before);
        p.set('page_size', '2000');
        return `credit-notes/?${p.toString()}`;
      },
      transformResponse: (res: PaginatedResponse<CreditNote> | CreditNote[]) =>
        Array.isArray(res) ? res : res.results,
      providesTags: ['CreditNote'],
    }),

    // Dashboard stats (aggregated server-side)
    getMarketplaceStats: builder.query<MarketplaceStats, MarketplaceStatsParams>({
      query: (params) => {
        const p = new URLSearchParams();
        if (params.status && params.status !== 'ALL') p.set('status', params.status);
        if (params.created_at_after)  p.set('created_at_after',  params.created_at_after);
        if (params.created_at_before) p.set('created_at_before', params.created_at_before);
        if (params.is_test !== undefined) p.set('is_test', String(params.is_test));
        if (params.date_issued_after)  p.set('date_issued_after',  params.date_issued_after);
        if (params.date_issued_before) p.set('date_issued_before', params.date_issued_before);
        return `stats/?${p.toString()}`;
      },
      providesTags: ['Stats'],
    }),

    getChurnedUsers: builder.query<ChurnResponse, ChurnParams>({
      query: (params) => {
        const p = new URLSearchParams();
        if (params.churned_after) p.set('churned_after', params.churned_after);
        if (params.churned_before) p.set('churned_before', params.churned_before);
        if (params.reason) p.set('reason', params.reason);
        if (params.app_activity) p.set('app_activity', params.app_activity);
        if (params.plan) p.set('plan', params.plan);
        if (params.search) p.set('search', params.search);
        if (params.page) p.set('page', String(params.page));
        if (params.page_size) p.set('page_size', String(params.page_size));
        const qs = p.toString();
        return qs ? `churn/?${qs}` : 'churn/';
      },
      providesTags: ['Churn'],
    }),
  }),
});

export const {
  useGetPlansQuery,
  useGetOrdersQuery,
  useGetSubscriptionsQuery,
  useGetCreditNotesQuery,
  useGetMarketplaceStatsQuery,
  useGetChurnedUsersQuery,
} = marketplaceApi;
